import React from "react";
import { createPortal } from "react-dom";
import { Flag, Plus, Rows3, Star, Table2 } from "lucide-react";
import { useLocation } from "react-router-dom";

import { useAccount, useTutorialStatus, useUpdateNativeTutorialStatus } from "@/queries/account";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { isNativeApp } from "@/lib/runtime";
import { NATIVE_ONBOARDING_VERSION } from "@/components/native/NativeOnboarding";

const FAVORITE_DIALOG_CLOSED_EVENT = "native-onboarding:favorite-team-dialog-closed";
const HIGHLIGHT_DELAY_MS = 650;
const HEAT_DIALOG_HIGHLIGHT_DELAY_MS = 600;

const HIGHLIGHTS = {
  favorite_team: {
    selector: '[data-native-onboarding-target="favorite-team"]',
    icon: Star,
    title: "Välj ditt favoritlag",
    text: "Anpassa startsidan med laget du följer.",
    action: "Välj lag",
  },
  create_protocol: {
    selector: '[data-tutorial-target="nav-new"]',
    icon: Plus,
    title: "Starta ditt matchprotokoll här",
    text: "Öppna protokollmenyn för att se matchkort och starta matchprotokoll.",
    action: "Jag förstår",
  },
  protocol_cards: {
    selector: '[data-native-onboarding-target="protocol-cards"]',
    icon: Rows3,
    title: "Följ heat för heat",
    text: "Vy Heatkort visar ett heat i taget.",
    action: "Visa tabellvyn",
  },
  protocol_table: {
    selector: '[data-native-onboarding-target="protocol-table"]',
    icon: Table2,
    title: "Se hela protokollet",
    text: "Tabellvyn ger en samlad överblick över förare, heat och poäng.",
    action: "Välj standardvy",
  },
  heat_dialog: {
    selector: '[data-native-onboarding-target="heat-result-controls"]',
    icon: Flag,
    title: "Registrera heatet",
    text: "Välj poäng och eventuell status för varje förare. Spara sedan heatets resultat.",
    action: "Jag förstår",
  },
};

export default function NativeOnboardingHighlights() {
  const { pathname } = useLocation();
  const { data: tutorialData, isLoading: tutorialLoading } = useTutorialStatus();
  const { data: account, isLoading: accountLoading } = useAccount();
  const updateTutorial = useUpdateNativeTutorialStatus();
  const { isOpen: protocolShellOpen, activeProtocolMatchId } = useProtocolShell();
  const [rect, setRect] = React.useState(null);
  const [targetReady, setTargetReady] = React.useState(false);
  const [delayComplete, setDelayComplete] = React.useState(false);
  const [waitingForFavoriteDialog, setWaitingForFavoriteDialog] = React.useState(false);

  const tutorial = tutorialData?.tutorial;
  const highlights = tutorial?.nativeHighlights || {};
  const onboardingComplete = Number(tutorial?.nativeOnboardingVersion || 0) >= NATIVE_ONBOARDING_VERSION;
  const favoriteAlreadyChosen = Boolean(account?.favorite_team_id);
  const currentName = !highlights.favorite_team && !favoriteAlreadyChosen
    ? "favorite_team"
    : !highlights.create_protocol
      ? "create_protocol"
      : !highlights.protocol_cards
        ? "protocol_cards"
        : !highlights.protocol_table
          ? "protocol_table"
          : !highlights.heat_dialog
            ? "heat_dialog"
            : null;
  const current = currentName ? HIGHLIGHTS[currentName] : null;
  const isHomeHighlight = ["favorite_team", "create_protocol"].includes(currentName);
  const isProtocolHighlight = ["protocol_cards", "protocol_table", "heat_dialog"].includes(currentName);
  const visible = (
    isNativeApp()
    && (
      (pathname === "/" && isHomeHighlight)
      || (isProtocolHighlight && protocolShellOpen && Boolean(activeProtocolMatchId))
    )
    && onboardingComplete
    && !tutorialLoading
    && !accountLoading
    && !waitingForFavoriteDialog
    && Boolean(current)
  );
  const delayedVisible = visible && targetReady && delayComplete;

  React.useEffect(() => {
    setTargetReady(false);
    if (!visible || !current) return undefined;

    const checkTarget = () => {
      const target = document.querySelector(current.selector);
      if (!target) return;
      const next = target.getBoundingClientRect();
      const style = window.getComputedStyle(target);
      if (next.width <= 0 || next.height <= 0 || style.display === "none" || style.visibility === "hidden") {
        return;
      }
      setTargetReady(true);
    };

    checkTarget();
    const observer = new MutationObserver(checkTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [current, visible]);

  React.useEffect(() => {
    setDelayComplete(false);
    if (!visible || !targetReady) return undefined;
    const delay = currentName === "heat_dialog"
      ? HEAT_DIALOG_HIGHLIGHT_DELAY_MS
      : HIGHLIGHT_DELAY_MS;
    const timer = window.setTimeout(() => setDelayComplete(true), delay);
    return () => window.clearTimeout(timer);
  }, [currentName, targetReady, visible]);

  React.useEffect(() => {
    if (!onboardingComplete || !favoriteAlreadyChosen || highlights.favorite_team) return;
    updateTutorial.mutate({ nativeHighlights: { favorite_team: true } });
  }, [favoriteAlreadyChosen, highlights.favorite_team, onboardingComplete]);

  React.useEffect(() => {
    const handleFavoriteDialogClosed = () => setWaitingForFavoriteDialog(false);
    window.addEventListener(FAVORITE_DIALOG_CLOSED_EVENT, handleFavoriteDialogClosed);
    return () => window.removeEventListener(FAVORITE_DIALOG_CLOSED_EVENT, handleFavoriteDialogClosed);
  }, []);

  React.useEffect(() => {
    if (!delayedVisible || !current) {
      setRect(null);
      return undefined;
    }
    const measure = () => {
      const target = document.querySelector(current.selector);
      if (!target) {
        setRect(null);
        return;
      }
      const next = target.getBoundingClientRect();
      const style = window.getComputedStyle(target);
      if (next.width <= 0 || next.height <= 0 || style.display === "none" || style.visibility === "hidden") {
        setRect(null);
        return;
      }
      setRect({ top: next.top, left: next.left, width: next.width, height: next.height });
    };
    const frame = window.requestAnimationFrame(measure);
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [current, delayedVisible]);

  const finish = async (activateTarget = false) => {
    if (!currentName || !current) return;
    try {
      await updateTutorial.mutateAsync({ nativeHighlights: { [currentName]: true } });
      if (activateTarget) {
        setWaitingForFavoriteDialog(true);
        window.requestAnimationFrame(() => document.querySelector(current.selector)?.click());
      } else if (currentName === "protocol_cards") {
        window.requestAnimationFrame(() => (
          document.querySelector(HIGHLIGHTS.protocol_table.selector)?.click()
        ));
      }
    } catch {
      // Keep the highlight visible if its completion could not be saved.
    }
  };

  if (!delayedVisible || !rect || !current || typeof document === "undefined") return null;
  const Icon = current.icon;
  const targetNearBottom = rect.top > window.innerHeight * 0.58;

  return createPortal(
    <div className="native-v3-onboarding-highlight" role="dialog" aria-modal="true" aria-labelledby="native-highlight-title">
      <div
        className="native-v3-onboarding-spotlight"
        style={{ top: rect.top - 5, left: rect.left - 5, width: rect.width + 10, height: rect.height + 10 }}
      />
      <section
        className="native-v3-onboarding-coachmark"
        data-position={targetNearBottom ? "above" : "below"}
        style={targetNearBottom
          ? { bottom: Math.max(104, window.innerHeight - rect.top + 14) }
          : { top: rect.top + rect.height + 14 }}
      >
        <span><Icon aria-hidden="true" /></span>
        <div><h2 id="native-highlight-title">{current.title}</h2><p>{current.text}</p></div>
        <div className="native-v3-onboarding-coachmark-actions">
          {currentName === "favorite_team" ? (
            <button type="button" onClick={() => finish(false)}>Inte nu</button>
          ) : null}
          <button type="button" onClick={() => finish(currentName === "favorite_team")}>{current.action}</button>
        </div>
      </section>
    </div>,
    document.body
  );
}
